import { Component, Input, ViewChild, OnInit, OnChanges, SimpleChanges, AfterViewInit, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, Upload, Loader2, ListFilter, CheckCircle2, XCircle, User, Scale, Search, X, MessageSquare, ClipboardList, Send } from 'lucide-angular';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AutomatedTestService } from '../../../core/services/automatedTest.service';
import { AuthService } from '../../../core/services/auth.service';
import { UserModel } from '../../../core/models/user';
import { take } from 'rxjs/operators';

export interface CsvRecord {
  id_conversacion: string;
  conversacion_modelo: string;
  descripcion_resultado_esperado: string;
  ejecutar: string;
  enviar_a: string;
  agente: string;
}

export interface FilterValues {
  searchText: string;
  selectedColumn: string;
}

@Component({
  selector: 'app-csv-preview',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatPaginatorModule, MatSortModule, MatButtonModule, MatInputModule, MatFormFieldModule, MatSelectModule, MatCheckboxModule, MatAutocompleteModule, FormsModule, LucideAngularModule, MatSnackBarModule],
  animations: [trigger('detailExpand', [state('collapsed', style({height: '0px', minHeight: '0'})), state('expanded', style({height: '*'})), transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))])],
  template: `
    <div class=" w-full border-t border-light-border dark:border-dark-border pt-6">
      <h2 class="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-4 pl-2">
        Vista Previa del Archivo: {{ fileName }}
      </h2>
    
      <div class="bg-light-surface-secondary dark:bg-dark-surface-secondary p-4 mb-4">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
            Total de registros: {{ csvRecords.length }}
            @if (getFilteredRecords().length !== csvRecords.length) {
              <span class="text-blue-600 dark:text-blue-400">
                ({{ getFilteredRecords().length }} filtrados)
              </span>
            }
          </span>
          <span class="text-sm text-light-text-secondary dark:text-dark-text-secondary">
            Archivo: {{ fileName }}
          </span>
        </div>
      </div>
    
      <div class="bg-light-surface dark:bg-dark-surface p-4 mb-4 ">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-base font-semibold text-light-text-primary dark:text-dark-text-primary flex items-center">
            <lucide-angular [img]="ListFilter" class="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400"></lucide-angular>
            Filtros
          </h3>
        </div>
    
        <div class="w-full">
          <mat-form-field appearance="outline" class="w-full mb-3">
            <!-- Campo normal para filtros generales -->
            @if (filterValues.selectedColumn !== 'agente' && filterValues.selectedColumn !== 'ejecutar') {
              <input
                matInput
                [(ngModel)]="filterValues.searchText"
                (keyup)="applyFilter()"
                placeholder="Escriba para filtrar..."
                autocomplete="off"
                class="text-base">
            }
    
            <!-- Campo con autocomplete para agentes -->
            @if (filterValues.selectedColumn === 'agente') {
              <input
                matInput
                [(ngModel)]="filterValues.searchText"
                (input)="onAgentInputChange()"
                placeholder="Buscar agente..."
                [matAutocomplete]="agentAutocomplete"
                autocomplete="off"
                class="text-base">
            }
    
            <!-- Campo con autocomplete para ejecutar -->
            @if (filterValues.selectedColumn === 'ejecutar') {
              <input
                matInput
                [(ngModel)]="filterValues.searchText"
                (input)="onEjecutarInputChange()"
                placeholder="Buscar estado..."
                [matAutocomplete]="ejecutarAutocomplete"
                autocomplete="off"
                class="text-base">
            }
    
            <mat-autocomplete #agentAutocomplete="matAutocomplete" (optionSelected)="onAgentSelected($event.option.value)">
              @for (agent of filteredAgents; track agent) {
                <mat-option [value]="agent">
                  {{ agent }}
                </mat-option>
              }
            </mat-autocomplete>
    
            <mat-autocomplete #ejecutarAutocomplete="matAutocomplete" (optionSelected)="onEjecutarSelected($event.option.value)">
              @for (estado of filteredEjecutarOptions; track estado) {
                <mat-option [value]="estado.value">
                  <span class="inline-flex items-center">
                    <lucide-angular [img]="estado.value === 'si' ? CheckCircle2 : XCircle" class="w-4 h-4 mr-2" [class]="estado.value === 'si' ? 'text-green-600' : 'text-red-600'"></lucide-angular>
                    {{ estado.label }}
                  </span>
                </mat-option>
              }
            </mat-autocomplete>
    
            <lucide-angular matSuffix [img]="filterValues.selectedColumn === 'agente' ? UserIcon : filterValues.selectedColumn === 'ejecutar' ? ScaleIcon : SearchIcon" class="w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary"></lucide-angular>
          </mat-form-field>
    
          <div class="flex flex-wrap items-center gap-3">
            <div class="flex items-center space-x-2">
              <mat-form-field appearance="outline" class="!text-sm min-w-48" subscriptSizing="dynamic">
                <mat-select
                  [(ngModel)]="filterValues.selectedColumn"
                  (selectionChange)="onColumnSelectionChange(); applyFilter()"
                  class="!text-sm"
                  panelClass="compact-select-panel">
                  @for (option of columnOptions; track option) {
                    <mat-option [value]="option.value" class="!text-sm !py-1 !min-h-8">
                      {{ option.label }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
            <div class="hidden md:block h-8 w-px bg-light-border dark:bg-dark-border"></div>
    
            <div class="flex items-center space-x-2">
              <mat-form-field appearance="outline" class="!text-sm min-w-44" subscriptSizing="dynamic">
                <mat-select
                  [(ngModel)]="selectedEnvironment"
                  [class]="selectedEnvironment ? '!text-sm' : '!text-sm !text-amber-600'"
                  class="!text-sm">
                  <mat-option value="">Seleccionar ambiente</mat-option>
                  @for (env of environments; track env) {
                    <mat-option [value]="env.value" class="!text-sm">
                      {{ env.label }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
    
            <div class="flex items-center gap-2 ml-auto mt-1 responsive-button-container">
              <button
                mat-stroked-button
                (click)="clearFilters()"
                class="text-sm !text-blue-600 dark:!text-blue-400 !border-blue-600 dark:!border-blue-400 hover:!bg-blue-50 dark:hover:!bg-blue-900/20 responsive-button">
                <lucide-angular [img]="XIcon" class="w-4 h-4 mr-1"></lucide-angular>
                Limpiar Filtros
              </button>
              <button
                mat-flat-button
                (click)="onProcessTests()"
                [disabled]="isCurrentlyProcessing || getExecutableTestsCount() === 0"
                class="!inline-flex !items-center !justify-center !px-4 !py-4 !text-sm !font-medium !text-white !rounded-md !shadow-sm !transition-colors responsive-button !whitespace-nowrap"
                [ngClass]="{
                  '!bg-indigo-400 !cursor-not-allowed': isCurrentlyProcessing || getExecutableTestsCount() === 0,
                  '!bg-indigo-600 hover:!bg-indigo-700 !cursor-pointer': !(isCurrentlyProcessing || getExecutableTestsCount() === 0)
                }">
    
                <span class="mt-1 inline-flex items-center">
                  @if (isCurrentlyProcessing) {
                    <lucide-icon
                      [img]="Loader2"
                      class="w-4 h-4 mr-2 animate-spin">
                    </lucide-icon>
                  }
                  @if (!isCurrentlyProcessing) {
                    <lucide-icon
                      [img]="Upload"
                      class="w-4 h-4 mr-2">
                    </lucide-icon>
                  }
                  {{ isCurrentlyProcessing ? 'Enviando...' : 'Enviar (' + getExecutableTestsCount() + ' de ' + csvRecords.length + ')' }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    
      <div class="overflow-x-auto">
        <mat-table
          [dataSource]="dataSource"
          matSort
          (matSortChange)="announceSortChange($event)"
          class="w-full bg-light-surface dark:bg-dark-surface rounded-lg shadow-sm"
          multiTemplateDataRows>
    
          <!-- Ejecutar Column -->
          <ng-container matColumnDef="ejecutar">
            <mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              sortActionDescription="Ordenar por estado: primero 'No ejecutar' luego 'Ejecutar'"
              class="!w-16 !max-w-16 !bg-transparent text-xs   uppercase tracking-wide text-light-text-secondary dark:text-dark-text-secondary font-medium !p-2"
              [title]="'Click para ordenar por estado de ejecución'">
              <div class="flex flex-col items-center justify-center relative z-10 gap-1">
                <mat-checkbox
                  [checked]="getAllCheckboxState().checked"
                  [indeterminate]="getAllCheckboxState().indeterminate"
                  (change)="toggleAllExecuteStatus()"
                  (click)="$event.stopPropagation()"
                  [title]="getAllStatusToggleTooltip()"
                  class="scale-75">
                </mat-checkbox>
              </div>
            </mat-header-cell>
            <mat-cell *matCellDef="let record" class="!w-16 !max-w-16 !bg-transparent text-sm text-light-text-primary dark:text-dark-text-primary !p-2">
              <div class="flex items-center justify-center relative z-10">
                <mat-checkbox
                  [checked]="record.ejecutar === 'si'"
                  (change)="toggleExecuteStatus(record)"
                  (click)="$event.stopPropagation()"
                  class="scale-75">
                </mat-checkbox>
              </div>
            </mat-cell>
          </ng-container>
    
          <!-- ID Conversación Column -->
          <ng-container matColumnDef="id_conversacion">
            <mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              sortActionDescription="Sort by conversation ID"
              class="text-xs uppercase tracking-wide text-light-text-secondary dark:text-dark-text-secondary bg-light-surface-secondary dark:bg-dark-surface-secondary font-medium">
              ID
            </mat-header-cell>
            <mat-cell *matCellDef="let record" class="text-sm text-light-text-primary dark:text-dark-text-primary font-medium">
              {{ record.id_conversacion }}
            </mat-cell>
          </ng-container>
    
          <!-- Conversación Modelo Column -->
          <ng-container matColumnDef="conversacion_modelo">
            <mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              sortActionDescription="Sort by model conversation"
              class="text-xs uppercase tracking-wide text-light-text-secondary dark:text-dark-text-secondary bg-light-surface-secondary dark:bg-dark-surface-secondary font-medium">
              Conversación Modelo
            </mat-header-cell>
            <mat-cell
              *matCellDef="let record"
              class="text-sm text-light-text-primary dark:text-dark-text-primary truncate"
              [title]="record.conversacion_modelo">
              {{ record.conversacion_modelo }}
            </mat-cell>
          </ng-container>
    
          <!-- Resultado Esperado Column -->
          <ng-container matColumnDef="descripcion_resultado_esperado">
            <mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              sortActionDescription="Sort by expected result"
              class="text-xs uppercase tracking-wide text-light-text-secondary dark:text-dark-text-secondary bg-light-surface-secondary dark:bg-dark-surface-secondary font-medium">
              Resultado Esperado
            </mat-header-cell>
            <mat-cell
              *matCellDef="let record"
              class="text-sm text-light-text-primary dark:text-dark-text-primary truncate"
              [title]="record.descripcion_resultado_esperado">
              {{ record.descripcion_resultado_esperado }}
            </mat-cell>
          </ng-container>
    
          <!-- Agente Column -->
          <ng-container matColumnDef="agente">
            <mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              sortActionDescription="Sort by agent"
              class="text-xs uppercase tracking-wide text-light-text-secondary dark:text-dark-text-secondary bg-light-surface-secondary dark:bg-dark-surface-secondary font-medium">
              Agente
            </mat-header-cell>
            <mat-cell *matCellDef="let record" class="text-sm text-light-text-primary dark:text-dark-text-primary">
              {{ record.agente }}
            </mat-cell>
          </ng-container>
    
          <!-- Enviar A Column -->
          <ng-container matColumnDef="enviar_a">
            <mat-header-cell
              *matHeaderCellDef
              mat-sort-header
              sortActionDescription="Sort by send to"
              class="text-xs uppercase tracking-wide text-light-text-secondary dark:text-dark-text-secondary bg-light-surface-secondary dark:bg-dark-surface-secondary font-medium">
              Enviar A
            </mat-header-cell>
            <mat-cell *matCellDef="let record" class="text-sm text-light-text-primary dark:text-dark-text-primary">
              {{ record.enviar_a }}
            </mat-cell>
          </ng-container>
    
          <!-- Expanded Content Template -->
          <ng-container matColumnDef="expandedDetail">
            <mat-cell *matCellDef="let record" [attr.colspan]="displayedColumns.length" class="!p-0 w-full">
              <div class="expanded-detail w-full" [@detailExpand]="record == expandedElement ? 'expanded' : 'collapsed'">
                <div class="w-full bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400">
                  <!-- Layout horizontal que siempre ocupa todo el ancho -->
                  <div class="flex flex-col sm:flex-row w-full h-full">
                    <!-- Conversación Modelo - Flex-1 para ocupar espacio disponible -->
                    <div class="flex-1 p-3 min-w-0">
                      <h4 class="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center">
                        <lucide-angular [img]="MessageSquare" class="w-4 h-4 mr-2"></lucide-angular>
                        Conversación Modelo:
                      </h4>
                      <div class="bg-light-surface dark:bg-dark-surface p-3 rounded-lg border border-blue-200 dark:border-blue-700 shadow-sm h-48 overflow-auto">
                        <p class="text-sm text-light-text-secondary dark:text-dark-text-secondary whitespace-pre-wrap leading-relaxed">{{ record.conversacion_modelo }}</p>
                      </div>
                    </div>
    
                    <!-- Separador vertical en pantallas grandes -->
                    <div class="hidden sm:block w-px bg-blue-200 dark:bg-blue-700 my-3"></div>
    
                    <!-- Resultado Esperado - Flex-1 para ocupar espacio disponible -->
                    <div class="flex-1 p-3 min-w-0">
                      <h4 class="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center">
                        <lucide-angular [img]="ClipboardList" class="w-4 h-4 mr-2"></lucide-angular>
                        Resultado Esperado:
                      </h4>
                      <div class="bg-light-surface dark:bg-dark-surface p-3 rounded-lg border border-blue-200 dark:border-blue-700 shadow-sm h-48 overflow-auto">
                        <p class="text-sm text-light-text-secondary dark:text-dark-text-secondary whitespace-pre-wrap leading-relaxed">{{ record.descripcion_resultado_esperado }}</p>
                      </div>
                    </div>
                  </div>
    
                  <!-- Información adicional compacta en línea -->
                  <div class="px-3 pb-3 border-t border-blue-200 dark:border-blue-700 bg-blue-25 dark:bg-blue-950/30">
                    <div class="flex flex-wrap items-center gap-6 py-2 w-full">
                      <div class="flex items-center gap-2 min-w-0">
                        <lucide-angular [img]="UserIcon" class="w-4 h-4 text-blue-800 dark:text-blue-200"></lucide-angular>
                        <span class="text-xs font-semibold text-blue-800 dark:text-blue-200">Agente:</span>
                        <span class="text-sm text-light-text-secondary dark:text-dark-text-secondary font-medium">{{ record.agente }}</span>
                      </div>
    
                      <div class="flex items-center gap-2 min-w-0">
                        <lucide-angular [img]="SendIcon" class="w-4 h-4 text-blue-800 dark:text-blue-200"></lucide-angular>
                        <span class="text-xs font-semibold text-blue-800 dark:text-blue-200">Enviar A:</span>
                        <span class="text-sm text-light-text-secondary dark:text-dark-text-secondary font-medium">{{ record.enviar_a }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </mat-cell>
          </ng-container>
    
    
          <mat-header-row *matHeaderRowDef="displayedColumns" class="bg-light-surface-secondary dark:bg-dark-surface-secondary"></mat-header-row>
          <mat-row
            *matRowDef="let row; columns: displayedColumns;"
            class="hover:bg-light-surface-hover dark:hover:bg-dark-surface-hover transition-colors cursor-pointer"
            [class.expanded-row]="expandedElement === row"
            (click)="toggleRow(row)">
          </mat-row>
          <mat-row
            *matRowDef="let row; columns: ['expandedDetail']"
            class="detail-row">
          </mat-row>
        </mat-table>
    
        <mat-paginator
          #paginator
          [pageSizeOptions]="pageSizeOptions"
          [pageSize]="defaultPageSize"
          showFirstLastButtons
          aria-label="Seleccionar página de pruebas automatizadas"
          class="bg-light-surface dark:bg-dark-surface border-t border-light-border dark:border-dark-border">
        </mat-paginator>
      </div>
    
      @if (csvRecords.length === 0) {
        <div class="text-center py-8">
          <p class="text-light-text-secondary dark:text-dark-text-secondary">
            No se encontraron registros en el archivo CSV.
          </p>
        </div>
      }
    </div>
    `,
  styles: [`
    .status-yes { @apply inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100; }
    .status-no { @apply inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100; }
    .expanded-detail { overflow: hidden; }
    .detail-row { min-height: 0; }
    .expanded-row { background: rgb(var(--light-surface-hover) / 1); }
    [data-theme="dark"] .expanded-row { background: rgb(var(--dark-surface-hover) / 1); }
    :host ::ng-deep .compact-select-panel .mat-mdc-option { min-height: 32px !important; padding: 4px 16px !important; line-height: 24px !important; }
    :host ::ng-deep .compact-select-panel { max-height: 240px !important; }
    
    /* Responsive Button Container - Stack vertically on narrow screens */
    .responsive-button-container {
      @media (max-width: 480px) {
        flex-direction: column !important;
        align-items: stretch !important;
        gap: 8px !important;
        width: 100% !important;
      }
    }

    .responsive-button {
      @media (max-width: 480px) {
        width: 100% !important;
        justify-content: center !important;
      }
    }

    /* Responsive Paginator Layout */
    @media (max-width: 480px) {
      :host ::ng-deep .mat-mdc-paginator { display: flex !important; flex-direction: column !important; align-items: stretch !important; padding: 12px 8px !important; gap: 8px !important; }
      :host ::ng-deep .mat-mdc-paginator-page-size, :host ::ng-deep .mat-mdc-paginator-range-label { display: flex !important; align-items: center !important; font-size: 12px !important; margin: 0 !important; }
      :host ::ng-deep .mat-mdc-paginator > *:not(.mat-mdc-paginator-range-actions) { display: inline-flex !important; align-items: center !important; }
      :host ::ng-deep .mat-mdc-paginator-page-size { justify-content: flex-start !important; flex: 0 0 auto !important; order: 1 !important; }
      :host ::ng-deep .mat-mdc-paginator-range-label { justify-content: flex-end !important; flex: 1 1 auto !important; text-align: right !important; order: 2 !important; margin-left: auto !important; }
      :host ::ng-deep .mat-mdc-paginator-page-size-label { display: none !important; }
      :host ::ng-deep .mat-mdc-paginator-page-size-select { margin: 0 !important; }
      :host ::ng-deep .mat-mdc-paginator-range-actions { display: flex !important; align-items: center !important; justify-content: center !important; width: 100% !important; margin-top: 8px !important; gap: 4px !important; order: 2 !important; }
      :host ::ng-deep .mat-mdc-paginator-navigation-first, :host ::ng-deep .mat-mdc-paginator-navigation-previous, :host ::ng-deep .mat-mdc-paginator-navigation-next, :host ::ng-deep .mat-mdc-paginator-navigation-last { min-width: 40px !important; width: 40px !important; height: 40px !important; margin: 0 2px !important; }
      :host ::ng-deep .mat-mdc-paginator-page-size, :host ::ng-deep .mat-mdc-paginator-range-label { position: relative !important; z-index: 2 !important; }
    }
    @media (max-width: 360px) {
      :host ::ng-deep .mat-mdc-paginator-range-label { font-size: 11px !important; }
      :host ::ng-deep .mat-mdc-paginator-navigation-first, :host ::ng-deep .mat-mdc-paginator-navigation-previous, :host ::ng-deep .mat-mdc-paginator-navigation-next, :host ::ng-deep .mat-mdc-paginator-navigation-last { min-width: 36px !important; width: 36px !important; height: 36px !important; }
    }
  `]
})
export class CsvPreviewComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() csvRecords: CsvRecord[] = [];
  @Input() fileName: string = '';
  @Output() recordsUpdated = new EventEmitter<CsvRecord[]>();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private internalProcessing = false;
  private readonly csvSeparator = ';';
  
  get isCurrentlyProcessing(): boolean { return this.internalProcessing; }

  selectedEnvironment: 'dev' | 'stg' | 'prd' | '' = '';
  dataSource = new MatTableDataSource<CsvRecord>();
  displayedColumns = ['ejecutar', 'id_conversacion', 'conversacion_modelo', 'descripcion_resultado_esperado', 'agente', 'enviar_a'];
  allColumns = ['ejecutar', 'id_conversacion', 'conversacion_modelo', 'descripcion_resultado_esperado', 'agente', 'enviar_a'];
  mobileColumns = ['ejecutar', 'id_conversacion', 'agente'];
  isMobile = false;
  expandedElement: CsvRecord | null = null;
  pageSizeOptions = [5, 10, 25, 50];
  defaultPageSize = 10;
  filterValues: FilterValues = { searchText: '', selectedColumn: 'all' };
  filteredAgents: string[] = [];
  uniqueAgents: string[] = [];
  filteredEjecutarOptions = [{ value: 'si', label: 'Ejecutar' }, { value: 'no', label: 'No ejecutar' }];

  readonly columnOptions = [
    { value: 'all', label: 'Filtrar por Columna' }, { value: 'id_conversacion', label: 'ID Conversación' },
    { value: 'conversacion_modelo', label: 'Conversación Modelo' }, { value: 'descripcion_resultado_esperado', label: 'Resultado Esperado' },
    { value: 'agente', label: 'Agente' }, { value: 'ejecutar', label: 'Ejecutar' }, { value: 'enviar_a', label: 'Enviar A' }
  ];
  readonly environments = [{ value: 'dev', label: 'Desarrollo' }, { value: 'stg', label: 'Staging' }, { value: 'prd', label: 'Producción' }];
  readonly ejecutarOptions = [{ value: 'si', label: 'Ejecutar' }, { value: 'no', label: 'No ejecutar' }];
  readonly Upload = Upload;
  readonly Loader2 = Loader2;
  readonly ListFilter = ListFilter;
  readonly CheckCircle2 = CheckCircle2;
  readonly XCircle = XCircle;
  readonly UserIcon = User;
  readonly ScaleIcon = Scale;
  readonly SearchIcon = Search;
  readonly XIcon = X;
  readonly MessageSquare = MessageSquare;
  readonly ClipboardList = ClipboardList;
  readonly SendIcon = Send;

  constructor(private _liveAnnouncer: LiveAnnouncer, private snackBar: MatSnackBar, 
              private automatedTestService: AutomatedTestService, private authService: AuthService) {}

  ngOnInit(): void {
    this.updateDataSource();
    this.checkScreenSize();
    this.updateUniqueAgents();
    this.dataSource.filterPredicate = (data: CsvRecord, filter: string) => {
      const filters: FilterValues = JSON.parse(filter);
      if (!filters.searchText) return true;
      const searchText = filters.searchText.toLowerCase();
      if (filters.selectedColumn === 'all') {
        return Object.values(data).some(value => value?.toString().toLowerCase().includes(searchText));
      }
      const columnValue = data[filters.selectedColumn as keyof CsvRecord]?.toString().toLowerCase() || '';
      return columnValue.includes(searchText);
    };
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void { this.checkScreenSize(); }

  private checkScreenSize(): void {
    const isMobileNow = window.innerWidth < 1024;
    if (this.isMobile !== isMobileNow) {
      this.isMobile = isMobileNow;
      this.displayedColumns = this.isMobile ? this.mobileColumns : this.allColumns;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['csvRecords'] && !changes['csvRecords'].firstChange) this.updateDataSource();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = this.getSortingValue.bind(this);
  }

  private getSortingValue(data: CsvRecord, sortHeaderId: string): any {
    if (sortHeaderId === 'ejecutar') {
      return data.ejecutar === 'si' ? 1 : 0;
    }
    return data[sortHeaderId as keyof CsvRecord];
  }

  private updateDataSource(): void {
    this.dataSource.data = this.csvRecords;
    this.updateUniqueAgents();
    if (this.paginator) this.dataSource.paginator = this.paginator;
    if (this.sort) this.dataSource.sort = this.sort;
  }

  private updateUniqueAgents(): void {
    this.uniqueAgents = [...new Set(this.csvRecords.map(record => record.agente))].filter(agent => agent.trim() !== '');
    this.filteredAgents = [...this.uniqueAgents];
  }

  applyFilter(): void {
    this.dataSource.filter = JSON.stringify(this.filterValues);
    if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
  }

  clearFilters(): void {
    this.filterValues = { searchText: '', selectedColumn: 'all' };
    this.filteredAgents = [...this.uniqueAgents];
    this.filteredEjecutarOptions = [...this.ejecutarOptions];
    this.applyFilter();
  }

  onAgentInputChange(): void {
    const searchValue = this.filterValues.searchText.toLowerCase();
    this.filteredAgents = this.uniqueAgents.filter(agent => agent.toLowerCase().includes(searchValue));
    this.applyFilter();
  }

  onAgentSelected(agent: string): void {
    this.filterValues.searchText = agent;
    this.applyFilter();
  }

  onEjecutarInputChange(): void {
    const searchValue = this.filterValues.searchText.toLowerCase();
    this.filteredEjecutarOptions = this.ejecutarOptions.filter(option => 
      option.label.toLowerCase().includes(searchValue) || option.value.toLowerCase().includes(searchValue));
    this.applyFilter();
  }

  onEjecutarSelected(value: string): void {
    this.filterValues.searchText = value;
    this.applyFilter();
  }

  onColumnSelectionChange(): void {
    this.filterValues.searchText = '';
    if (this.filterValues.selectedColumn === 'agente') this.filteredAgents = [...this.uniqueAgents];
    else if (this.filterValues.selectedColumn === 'ejecutar') this.filteredEjecutarOptions = [...this.ejecutarOptions];
  }

  getFilteredRecords(): CsvRecord[] { return this.dataSource.filteredData; }
  getExecutableTestsCount(): number { return this.csvRecords.filter(record => record.ejecutar === 'si').length; }
  announceSortChange(sortState: Sort): void { this._liveAnnouncer.announce(sortState.direction ? `Sorted ${sortState.direction}ending` : 'Sorting cleared'); }
  toggleRow(record: CsvRecord): void { this.expandedElement = this.expandedElement === record ? null : record; }
  onProcessTests(): void { 
    if (!this.selectedEnvironment) {
      this.snackBar.open('Por favor seleccione un ambiente antes de ejecutar las pruebas.', '', {
        duration: 4000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar']
      });
      return;
    }

    const recordsToExecute = this.csvRecords.filter(record => record.ejecutar === 'si');
    if (recordsToExecute.length === 0) {
      this.snackBar.open('No hay registros seleccionados para ejecutar.', '', {
        duration: 4000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar']
      });
      return;
    }

    this.internalProcessing = true;
    this.automatedTestService.sendCsvFileToApi(this.createFilteredCsvFile(recordsToExecute), this.selectedEnvironment).subscribe({
      next: () => {
        this.internalProcessing = false;
        const uniqueEmails = [...new Set(recordsToExecute.map(record => record.enviar_a).filter(email => email.trim()))];
        this.authService.user$.pipe(take(1)).subscribe((user: UserModel) => {
          const allEmails = [user?.email || 'usuario', ...uniqueEmails].filter((email, index, arr) => arr.indexOf(email) === index);
          const emailsText = allEmails.length > 3 ? `${allEmails.slice(0, 3).join(', ')} y ${allEmails.length - 3} más` : allEmails.join(', ');
          this.snackBar.open(`✅ ${recordsToExecute.length} pruebas enviadas correctamente. Los resultados llegarán a: ${emailsText}`, '', {
            duration: 8000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['success-snackbar']
          });
        });
      },
      error: (error) => {
        this.internalProcessing = false;
        let errorMessage = 'Ocurrió un error al enviar las pruebas.';
        if (error.status) errorMessage += ` (Código: ${error.status})`;
        if (error.error && typeof error.error === 'string') errorMessage += `\n\nDetalle: ${error.error}`;
        else if (error.message) errorMessage += `\n\nDetalle: ${error.message}`;
        errorMessage += '\n\nPor favor intente nuevamente o contacte al administrador del sistema.';
        this.snackBar.open(`❌ ${errorMessage}`, '', {
          duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar']
        });
      }
    });
  }

  toggleExecuteStatus(record: CsvRecord): void {
    const realIndex = this.findRecordIndex(record);
    if (realIndex !== -1) {
      const newStatus = this.csvRecords[realIndex].ejecutar === 'si' ? 'no' : 'si';
      this.csvRecords[realIndex].ejecutar = record.ejecutar = newStatus;
      this.dataSource.data = [...this.csvRecords];
      this.recordsUpdated.emit(this.csvRecords);
    }
  }

  toggleAllExecuteStatus(): void {
    const { yesCount, totalCount, filteredRecords } = this.getExecutionStats();
    
    // If all filtered records are selected, deselect all filtered records. Otherwise, select all filtered records.
    const newStatus = yesCount === totalCount ? 'no' : 'si';
    
    // Only update the filtered records, not all records
    let updatedCount = 0;
    filteredRecords.forEach(filteredRecord => {
      const realIndex = this.findRecordIndex(filteredRecord);
      if (realIndex !== -1) {
        this.csvRecords[realIndex].ejecutar = newStatus;
        updatedCount++;
      }
    });
    
    // Update the data source to reflect changes
    this.dataSource.data = [...this.csvRecords];
    this.recordsUpdated.emit(this.csvRecords);
  }

  private findRecordIndex(record: CsvRecord): number {
    // Find by exact object reference first (most reliable)
    let index = this.csvRecords.indexOf(record);
    if (index !== -1) return index;
    
    // If not found by reference, find by unique combination of all fields
    return this.csvRecords.findIndex(r => 
      r.id_conversacion === record.id_conversacion && 
      r.conversacion_modelo === record.conversacion_modelo &&
      r.descripcion_resultado_esperado === record.descripcion_resultado_esperado && 
      r.agente === record.agente &&
      r.enviar_a === record.enviar_a
    );
  }

  private getExecutionStats() {
    const filteredRecords = this.getFilteredRecords();
    const yesCount = filteredRecords.filter(record => record.ejecutar === 'si').length;
    const totalCount = filteredRecords.length;
    const hasFilter = this.filterValues.searchText.trim() !== '' || this.filterValues.selectedColumn !== 'all';
    return { yesCount, totalCount, hasFilter, filteredRecords };
  }

  getAllCheckboxState(): { checked: boolean; indeterminate: boolean } {
    const { yesCount, totalCount } = this.getExecutionStats();
    if (totalCount === 0 || yesCount === 0) return { checked: false, indeterminate: false };
    if (yesCount === totalCount) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  }

  getAllStatusToggleTooltip(): string {
    const { yesCount, totalCount, hasFilter } = this.getExecutionStats();
    if (totalCount === 0) return 'No hay registros para seleccionar';
    const suffix = hasFilter ? ' registros filtrados' : ' registros';
    if (yesCount === totalCount) return `Deseleccionar todos los ${totalCount}${suffix}`;
    if (yesCount === 0) return `Seleccionar todos los ${totalCount}${suffix}`;
    return `Seleccionar todos los ${totalCount}${suffix} (${yesCount} ya seleccionados)`;
  }

  private createFilteredCsvFile(recordsToExecute: CsvRecord[]): File {
    const headers = ['id_conversacion', 'conversacion_modelo', 'descripcion_resultado_esperado', 'agente', 'ejecutar', 'enviar_a'];
    const csvRows = [headers.join(this.csvSeparator)];
    recordsToExecute.forEach(record => {
      const row = [record.id_conversacion, record.conversacion_modelo, record.descripcion_resultado_esperado, 
                   record.agente, record.ejecutar, record.enviar_a].map(field => this.escapeCsvField(field));
      csvRows.push(row.join(this.csvSeparator));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    return new File([blob], this.fileName.replace('.csv', '_filtered.csv'), { type: 'text/csv' });
  }

  private escapeCsvField(field: string): string {
    if (field.includes(this.csvSeparator) || field.includes('"') || field.includes('\n') || field.includes('\r')) {
      return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
  }
}